import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { ActionBar, Button, FieldShell, Tile } from "@fawkes/ui";

// products-migration Story 5.3 (frontend): the Entingen tab — the ENTING-product type-specific surface,
// shown next to "Product" only when the product's Type = ENTING. It is the React replacement for the
// Jmix EntingenTabHost iframe (visual baseline captures 11–12).
//
// Read: GET producten/{id}/entingen → { productId, productNaam, version, config{23 fields}, diersoorten,
// entingsProducten }. version === null means "no config row yet" (create-on-first-save); the config
// block still carries the lazy defaults so the tab opens populated. A non-ENTING/unknown product 404s.
//
// Write: PUT producten/{id}/entingen with { version, ...23 fields }. On success the local version is
// re-synced from the response (so a follow-on save won't 409); a 409 surfaces the "gewijzigd door iemand
// anders" message (same idiom as the Product tab). ROLE_PRODUCT_EDIT is enforced server-side; no client
// authority signal, so the affordance stays enabled and a 403 surfaces as a save error.
//
// The tab opens DIRECTLY EDITABLE (Jmix parity, matching the Product tab) with a tab-level
// Annuleren/Opslaan footer. The Entingen tab owns its own form state so the two tabs never half-save each
// other. "Annuleren" reverts unsaved edits (re-seeds the form from the last-loaded config) and stays on
// the tab — there is no navigation owner here, so a revert-in-place is the sane cancel.
//
// All urls are RELATIVE — the dataProvider prepends /api once (never `${apiUrl}/x` → /api/api).

interface Optie {
  id: number;
  label: string;
}

// The 23 editable config fields, faithful to EntingenConfigResponse.EntingenConfig.
interface EntingenConfig {
  diersoortId: number | null;
  vanafLeeftijdWaarde: number | null;
  vanafLeeftijdEenheid: string | null;
  totLeeftijdWaarde: number | null;
  totLeeftijdEenheid: string | null;
  toepassenAanwezig: boolean | null;
  toepassenPleeggezin: boolean | null;
  geldigheidsduurWaarde: number | null;
  geldigheidsduurEenheid: string | null;
  waarschuwingVoorafWaarde: number | null;
  waarschuwingVoorafEenheid: string | null;
  verplicht: boolean | null;
  vrijstellingToegestaan: boolean | null;
  vrijstellingReden: string | null;
  vervolgType: string | null;
  vervolgProductId: number | null;
  vervolgMomentDagen: number | null;
  combineerbaar: boolean | null;
  interneOpmerking: string | null;
  actief: boolean | null;
  notities: string | null;
  geldigVanafBasis: string | null;
  geldigTotBerekening: string | null;
}

interface EntingenResponse {
  productId: number;
  productNaam: string;
  version: number | null;
  config: EntingenConfig;
  diersoorten: Optie[];
  entingsProducten: Optie[];
}

// The mutable form mirror: numbers live as strings while typing ("" = cleared/preserve).
interface EntForm {
  diersoortId: string;
  vanafLeeftijdWaarde: string;
  vanafLeeftijdEenheid: string;
  totLeeftijdWaarde: string;
  totLeeftijdEenheid: string;
  toepassenAanwezig: boolean;
  toepassenPleeggezin: boolean;
  geldigheidsduurWaarde: string;
  geldigheidsduurEenheid: string;
  waarschuwingVoorafWaarde: string;
  waarschuwingVoorafEenheid: string;
  verplicht: boolean;
  vrijstellingToegestaan: boolean;
  vrijstellingReden: string;
  vervolgType: string;
  vervolgProductId: string;
  vervolgMomentDagen: string;
  combineerbaar: boolean;
  interneOpmerking: string;
  actief: boolean;
  notities: string;
  geldigVanafBasis: string;
  geldigTotBerekening: string;
}

// Unit vocab faithful to the Jmix tab / EntingAiSeedService: leeftijd = weken/maanden/jaren;
// waarschuwing = dagen/weken/maanden.
const LEEFTIJD_EENHEDEN = ["weken", "maanden", "jaren"] as const;
const WAARSCHUWING_EENHEDEN = ["dagen", "weken", "maanden"] as const;
const VERVOLG_TYPES = ["GEEN", "ZELFDE", "ANDERE"] as const;

// Geldigheidsduur is a free number + unit, exactly as `frontend-react/entingen` (the module that runs on
// acc) has it. It used to be six fixed presets here, which silently rewrote any stored value that wasn't
// one of them ("8 weken" -> "1 jaar") on open+save — destroying the setting. Never collapse this again.
const GELDIGHEIDSDUUR_EENHEDEN = ["dagen", "weken", "maanden", "jaren"] as const;

// The source's DEFAULT_VRIJSTELLINGSREDENEN, verbatim (it lets the host override them; we don't have that
// channel, so these are the list).
const VRIJSTELLINGSREDENEN = [
  "Medische contra-indicatie",
  "Recent elders gevaccineerd",
  "Titerbepaling voldoende",
  "Eigenaar weigert",
  "Anders",
];

// Mirror of the source's NORM_INTERVAL: stored units arrive in singular, plural or adverb form and are
// normalised to the plural the select offers, defaulting to "jaren".
const NORM_INTERVAL: Record<string, string> = {
  jaar: "jaren", jaren: "jaren", jaarlijks: "jaren",
  maand: "maanden", maanden: "maanden", maandelijks: "maanden",
  week: "weken", weken: "weken", wekelijks: "weken",
  dag: "dagen", dagen: "dagen", dagelijks: "dagen",
};

const NORM_WAARSCHUWING: Record<string, string> = {
  dag: "dagen", dagen: "dagen",
  week: "weken", weken: "weken",
  maand: "maanden", maanden: "maanden",
};

function normEenheid(map: Record<string, string>, value: string | null, fallback: string): string {
  return (value && map[value.trim().toLowerCase()]) || fallback;
}

function toForm(c: EntingenConfig): EntForm {
  const numStr = (n: number | null) => (n == null ? "" : String(n));
  return {
    diersoortId: numStr(c.diersoortId),
    vanafLeeftijdWaarde: numStr(c.vanafLeeftijdWaarde),
    vanafLeeftijdEenheid: c.vanafLeeftijdEenheid ?? "weken",
    totLeeftijdWaarde: numStr(c.totLeeftijdWaarde),
    // Source `DEFAULT_CONFIG`: a new/unset config opens with "jaren", not "weken".
    totLeeftijdEenheid: c.totLeeftijdEenheid ?? "jaren",
    toepassenAanwezig: c.toepassenAanwezig ?? true,
    toepassenPleeggezin: c.toepassenPleeggezin ?? true,
    geldigheidsduurWaarde: numStr(c.geldigheidsduurWaarde),
    geldigheidsduurEenheid: normEenheid(NORM_INTERVAL, c.geldigheidsduurEenheid, "jaren"),
    waarschuwingVoorafWaarde: numStr(c.waarschuwingVoorafWaarde),
    waarschuwingVoorafEenheid: normEenheid(NORM_WAARSCHUWING, c.waarschuwingVoorafEenheid, "dagen"),
    // Source `DEFAULT_CONFIG`: verplicht + vrijstellingToegestaan default to true for a new config.
    verplicht: c.verplicht ?? true,
    vrijstellingToegestaan: c.vrijstellingToegestaan ?? true,
    vrijstellingReden: c.vrijstellingReden ?? "",
    vervolgType: c.vervolgType ?? "GEEN",
    vervolgProductId: numStr(c.vervolgProductId),
    vervolgMomentDagen: numStr(c.vervolgMomentDagen),
    combineerbaar: c.combineerbaar ?? true,
    interneOpmerking: c.interneOpmerking ?? "",
    actief: c.actief ?? true,
    notities: c.notities ?? "",
    geldigVanafBasis: c.geldigVanafBasis ?? "TOEDIENINGSDATUM",
    geldigTotBerekening: c.geldigTotBerekening ?? "GELDIGHEIDSDUUR",
  };
}

// A Ja/Nee toggle row (Jmix capture 11/12: green switch + "Ja"/"Nee" text).
function YesNoToggle({
  checked,
  onChange,
  label,
  disabled,
  jaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  /** Overrides the "Ja" on-label (source `jaLabel`) — e.g. "Ja, verplicht" for the verplicht toggle. */
  jaLabel?: string;
}) {
  const t = useTranslate();
  return (
    <span className="entingen-toggle">
      <label className="fui-toggle product-toggle">
        <input
          type="checkbox"
          role="switch"
          className="fui-toggle__input"
          checked={checked}
          disabled={disabled}
          aria-label={label}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="fui-toggle__track" aria-hidden="true">
          <span className="fui-toggle__thumb" />
        </span>
      </label>
      <span className="entingen-toggle__text">
        {checked ? (jaLabel ?? t("entingenTab.toggle.ja")) : t("entingenTab.toggle.nee")}
      </span>
    </span>
  );
}

export function EntingenTab({ id }: { id: string }) {
  const t = useTranslate();

  const { data, isLoading, isError } = useCustom<EntingenResponse>({
    url: `producten/${id}/entingen`,
    method: "get",
  });
  const { mutate, isLoading: saving } = useCustomMutation();

  // Opens directly editable (Jmix parity). The form is seeded from the loaded config in the effect below.
  const [editing, setEditing] = useState(true);
  const [form, setForm] = useState<EntForm | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [notice, setNotice] = useState<"saved" | "error" | "conflict" | null>(null);

  const payload = data?.data;

  useEffect(() => {
    if (payload) {
      setVersion(payload.version ?? null);
      setForm(toForm(payload.config));
      setEditing(true);
    }
  }, [payload?.productId, payload?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError || !payload) {
    return <p className="fawkes-error">{t("entingenTab.loadFailed")}</p>;
  }

  const diersoorten = payload.diersoorten ?? [];
  const entingsProducten = payload.entingsProducten ?? [];

  // Annuleren: discard unsaved edits by re-seeding the form from the last-loaded config, staying on the
  // tab (no navigation owner here, so a revert-in-place is the cancel).
  const cancelEdit = () => {
    setForm(toForm(payload.config));
    setNotice(null);
  };
  const set = <K extends keyof EntForm>(key: K, value: EntForm[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setNotice(null);
  };

  // The human geldigheidsduur string for the info line — "{waarde} {eenheid}", singularised the way the
  // source does ("1 jaar", not "1 jaren").
  const geldigheidsduurLabel = (waarde: string | number | null, eenheid: string) =>
    `${waarde ?? 0} ${eenheidLabelVoor(waarde, eenheid)}`;

  const save = () => {
    if (!form) return;
    const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));
    const idOrNull = (v: string) => (v === "" ? null : Number(v));
    const trimOrNull = (v: string) => (v.trim() === "" ? null : v);

    mutate(
      {
        url: `producten/${id}/entingen`,
        method: "put",
        values: {
          version,
          // Toepassing
          diersoortId: idOrNull(form.diersoortId),
          vanafLeeftijdWaarde: numOrNull(form.vanafLeeftijdWaarde),
          vanafLeeftijdEenheid: form.vanafLeeftijdEenheid,
          totLeeftijdWaarde: numOrNull(form.totLeeftijdWaarde),
          totLeeftijdEenheid: form.totLeeftijdEenheid,
          toepassenAanwezig: form.toepassenAanwezig,
          toepassenPleeggezin: form.toepassenPleeggezin,
          // Geldigheidsduur — free number + unit, written through untouched.
          geldigheidsduurWaarde: numOrNull(form.geldigheidsduurWaarde),
          geldigheidsduurEenheid: form.geldigheidsduurEenheid,
          waarschuwingVoorafWaarde: numOrNull(form.waarschuwingVoorafWaarde),
          waarschuwingVoorafEenheid: form.waarschuwingVoorafEenheid,
          // Verplichting
          verplicht: form.verplicht,
          vrijstellingToegestaan: form.vrijstellingToegestaan,
          vrijstellingReden: trimOrNull(form.vrijstellingReden),
          // Vervolgenting
          vervolgType: form.vervolgType,
          vervolgProductId: form.vervolgType === "ANDERE" ? idOrNull(form.vervolgProductId) : null,
          vervolgMomentDagen: form.vervolgType === "GEEN" ? null : numOrNull(form.vervolgMomentDagen),
          // Overige instellingen
          combineerbaar: form.combineerbaar,
          interneOpmerking: trimOrNull(form.interneOpmerking),
          actief: form.actief,
          // Notities
          notities: trimOrNull(form.notities),
          // Geldigheidsregeling
          geldigVanafBasis: form.geldigVanafBasis,
          geldigTotBerekening: form.geldigTotBerekening,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          // Stay editable after save (Jmix parity): re-sync the version + re-seed the form from the
          // returned config, surface "Opgeslagen". A null config (shouldn't happen) leaves the form as-is.
          const updated = res.data as unknown as EntingenResponse;
          setVersion(updated?.version ?? null);
          if (updated?.config) setForm(toForm(updated.config));
          setNotice("saved");
        },
        onError: (err) => {
          setNotice((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error");
        },
      },
    );
  };

  const noticeText =
    notice === "saved"
      ? t("entingenTab.saved")
      : notice === "conflict"
        ? t("entingenTab.saveConflict")
        : notice === "error"
          ? t("entingenTab.saveError")
          : null;

  // ── Read-mode value resolution (the displayed config when not editing) ──
  const c = payload.config;
  const dash = "—";
  const diersoortLabel =
    c.diersoortId == null
      ? t("entingenTab.diersoort.all")
      : (diersoorten.find((d) => d.id === c.diersoortId)?.label ?? String(c.diersoortId));
  const vervolgProductLabel =
    c.vervolgProductId == null
      ? dash
      : (entingsProducten.find((e) => e.id === c.vervolgProductId)?.label ?? String(c.vervolgProductId));

  // Step preview (Jmix capture 11/12) — derived client-side from vervolgType (+ vervolgMomentDagen text).
  const vervolgTypeNow = editing && form ? form.vervolgType : (c.vervolgType ?? "GEEN");
  const vervolgMomentNow = editing && form ? form.vervolgMomentDagen : numToStr(c.vervolgMomentDagen);
  const showStap2 = vervolgTypeNow === "ZELFDE" || vervolgTypeNow === "ANDERE";

  const eenheidLabel = (u: string) => {
    const key = `entingenTab.eenheid.${u}`;
    const resolved = t(key);
    return resolved === key ? u : resolved;
  };

  // The source singularises at 1 ("1 jaar", not "1 jaren") — mirror that.
  const eenheidLabelVoor = (waarde: string | number | null, u: string) => {
    if (Number(waarde) !== 1) return eenheidLabel(u);
    const key = `entingenTab.eenheidEnkelvoud.${u}`;
    const resolved = t(key);
    return resolved === key ? eenheidLabel(u) : resolved;
  };

  return (
    <div className="entingen-tab">
      <div className="entingen-grid">
        {/* ── Toepassing ── */}
        <Tile title={t("entingenTab.section.toepassing")}>
          <div className="entingen-section">
            <FieldShell label={t("entingenTab.diersoort")}>
              {editing && form ? (
                <select
                  className="fui-field__input fui-select"
                  value={form.diersoortId}
                  onChange={(e) => set("diersoortId", e.target.value)}
                >
                  <option value="">{t("entingenTab.diersoort.all")}</option>
                  {diersoorten.map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="entingen-readonly">{diersoortLabel}</span>
              )}
            </FieldShell>

            <FieldShell label={t("entingenTab.vanafLeeftijd")}>
              {editing && form ? (
                <div className="entingen-waarde-eenheid">
                  <input
                    type="number"
                    className="fui-field__input"
                    value={form.vanafLeeftijdWaarde}
                    onChange={(e) => set("vanafLeeftijdWaarde", e.target.value)}
                  />
                  <select
                    className="fui-field__input fui-select"
                    value={form.vanafLeeftijdEenheid}
                    onChange={(e) => set("vanafLeeftijdEenheid", e.target.value)}
                  >
                    {LEEFTIJD_EENHEDEN.map((u) => (
                      <option key={u} value={u}>
                        {eenheidLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <span className="entingen-readonly">
                  {c.vanafLeeftijdWaarde == null
                    ? dash
                    : `${c.vanafLeeftijdWaarde} ${eenheidLabel(c.vanafLeeftijdEenheid ?? "weken")}`}
                </span>
              )}
            </FieldShell>

            <FieldShell label={t("entingenTab.totLeeftijd")}>
              {editing && form ? (
                <div className="entingen-waarde-eenheid">
                  <input
                    type="number"
                    className="fui-field__input"
                    placeholder="—"
                    value={form.totLeeftijdWaarde}
                    onChange={(e) => set("totLeeftijdWaarde", e.target.value)}
                  />
                  <select
                    className="fui-field__input fui-select"
                    value={form.totLeeftijdEenheid}
                    onChange={(e) => set("totLeeftijdEenheid", e.target.value)}
                  >
                    {LEEFTIJD_EENHEDEN.map((u) => (
                      <option key={u} value={u}>
                        {eenheidLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <span className="entingen-readonly">
                  {c.totLeeftijdWaarde == null
                    ? dash
                    : `${c.totLeeftijdWaarde} ${eenheidLabel(c.totLeeftijdEenheid ?? "weken")}`}
                </span>
              )}
            </FieldShell>
            <p className="fawkes-muted entingen-helper">{t("entingenTab.totLeeftijd.helper")}</p>

            <fieldset className="entingen-fieldset">
              <legend className="entingen-legend">{t("entingenTab.toepassenStatus")}</legend>
              <label className="entingen-check">
                <input
                  type="checkbox"
                  checked={editing && form ? form.toepassenAanwezig : (c.toepassenAanwezig ?? false)}
                  disabled={!editing}
                  onChange={(e) => set("toepassenAanwezig", e.target.checked)}
                />
                {t("entingenTab.toepassenAanwezig")}
              </label>
              <label className="entingen-check">
                <input
                  type="checkbox"
                  checked={editing && form ? form.toepassenPleeggezin : (c.toepassenPleeggezin ?? false)}
                  disabled={!editing}
                  onChange={(e) => set("toepassenPleeggezin", e.target.checked)}
                />
                {t("entingenTab.toepassenPleeggezin")}
              </label>
            </fieldset>
          </div>
        </Tile>

        {/* ── Geldigheidsduur & herhaling ── */}
        <Tile title={t("entingenTab.section.geldigheidsduur")}>
          <div className="entingen-section">
            <FieldShell label={t("entingenTab.geldigheidsduur")}>
              {editing && form ? (
                <div className="entingen-waarde-eenheid">
                  <input
                    type="number"
                    min="1"
                    className="fui-field__input"
                    value={form.geldigheidsduurWaarde}
                    onChange={(e) => set("geldigheidsduurWaarde", e.target.value)}
                  />
                  <select
                    className="fui-field__input fui-select"
                    value={form.geldigheidsduurEenheid}
                    onChange={(e) => set("geldigheidsduurEenheid", e.target.value)}
                  >
                    {GELDIGHEIDSDUUR_EENHEDEN.map((u) => (
                      <option key={u} value={u}>
                        {eenheidLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <span className="entingen-readonly">
                  {geldigheidsduurLabel(
                    c.geldigheidsduurWaarde,
                    normEenheid(NORM_INTERVAL, c.geldigheidsduurEenheid, "jaren"),
                  )}
                </span>
              )}
            </FieldShell>
            <p className="entingen-info entingen-info--blue">
              {t("entingenTab.geldigheidsduur.info", {
                args: [
                  editing && form
                    ? geldigheidsduurLabel(form.geldigheidsduurWaarde, form.geldigheidsduurEenheid)
                    : geldigheidsduurLabel(
                        c.geldigheidsduurWaarde,
                        normEenheid(NORM_INTERVAL, c.geldigheidsduurEenheid, "jaren"),
                      ),
                ],
              })}
            </p>

            <FieldShell label={t("entingenTab.waarschuwingVooraf")}>
              {editing && form ? (
                <div className="entingen-waarde-eenheid">
                  <input
                    type="number"
                    className="fui-field__input"
                    value={form.waarschuwingVoorafWaarde}
                    onChange={(e) => set("waarschuwingVoorafWaarde", e.target.value)}
                  />
                  <select
                    className="fui-field__input fui-select"
                    value={form.waarschuwingVoorafEenheid}
                    onChange={(e) => set("waarschuwingVoorafEenheid", e.target.value)}
                  >
                    {WAARSCHUWING_EENHEDEN.map((u) => (
                      <option key={u} value={u}>
                        {eenheidLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <span className="entingen-readonly">
                  {c.waarschuwingVoorafWaarde == null
                    ? dash
                    : `${c.waarschuwingVoorafWaarde} ${eenheidLabel(c.waarschuwingVoorafEenheid ?? "dagen")}`}
                </span>
              )}
            </FieldShell>
            <p className="fawkes-muted entingen-helper">
              {t("entingenTab.waarschuwingVooraf.helper", {
                args: [
                  editing && form
                    ? `${form.waarschuwingVoorafWaarde || "—"} ${eenheidLabel(form.waarschuwingVoorafEenheid)}`
                    : c.waarschuwingVoorafWaarde == null
                      ? dash
                      : `${c.waarschuwingVoorafWaarde} ${eenheidLabel(c.waarschuwingVoorafEenheid ?? "dagen")}`,
                ],
              })}
            </p>
          </div>
        </Tile>

        {/* ── Verplichting ── */}
        <Tile title={t("entingenTab.section.verplichting")}>
          <div className="entingen-section">
            <FieldShell label={t("entingenTab.verplicht")}>
              <YesNoToggle
                checked={editing && form ? form.verplicht : (c.verplicht ?? true)}
                onChange={(v) => set("verplicht", v)}
                label={t("entingenTab.verplicht")}
                jaLabel={t("entingenTab.toggle.jaVerplicht")}
                disabled={!editing}
              />
            </FieldShell>
            <FieldShell label={t("entingenTab.vrijstelling")}>
              <YesNoToggle
                checked={editing && form ? form.vrijstellingToegestaan : (c.vrijstellingToegestaan ?? true)}
                onChange={(v) => set("vrijstellingToegestaan", v)}
                label={t("entingenTab.vrijstelling")}
                disabled={!editing}
              />
            </FieldShell>
            {/* Only shown when an exemption is allowed, as in the source. It had no control at all here,
                while `vrijstellingReden` was still being sent on save — so the stored reason was invisible
                and unsettable. */}
            {(editing && form ? form.vrijstellingToegestaan : (c.vrijstellingToegestaan ?? true)) && (
              <FieldShell label={t("entingenTab.vrijstellingReden")}>
                {editing && form ? (
                  <select
                    className="fui-field__input fui-select"
                    value={form.vrijstellingReden}
                    onChange={(e) => set("vrijstellingReden", e.target.value)}
                  >
                    <option value="">{t("entingenTab.vrijstellingReden.placeholder")}</option>
                    {VRIJSTELLINGSREDENEN.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="entingen-readonly">{c.vrijstellingReden || "—"}</span>
                )}
              </FieldShell>
            )}
          </div>
        </Tile>

        {/* ── Vervolgenting (radios + step preview) ── */}
        <Tile title={t("entingenTab.section.vervolg")}>
          <div className="entingen-vervolg">
            <div className="entingen-vervolg__radios" role="radiogroup">
              {VERVOLG_TYPES.map((opt) => (
                <label key={opt} className="product-radio">
                  <input
                    type="radio"
                    name="vervolgType"
                    value={opt}
                    checked={vervolgTypeNow === opt}
                    disabled={!editing}
                    onChange={() => set("vervolgType", opt)}
                  />
                  {t(`entingenTab.vervolg.${opt.toLowerCase()}`)}
                </label>
              ))}
              {editing && form && form.vervolgType === "ANDERE" && (
                <FieldShell label={t("entingenTab.vervolg.product")}>
                  <select
                    className="fui-field__input fui-select"
                    value={form.vervolgProductId}
                    onChange={(e) => set("vervolgProductId", e.target.value)}
                  >
                    <option value="">{dash}</option>
                    {entingsProducten.map((e) => (
                      <option key={e.id} value={String(e.id)}>
                        {e.label}
                      </option>
                    ))}
                  </select>
                </FieldShell>
              )}
              {editing && form && form.vervolgType !== "GEEN" && (
                <FieldShell label={t("entingenTab.vervolg.moment")}>
                  <input
                    type="number"
                    className="fui-field__input"
                    value={form.vervolgMomentDagen}
                    onChange={(e) => set("vervolgMomentDagen", e.target.value)}
                  />
                </FieldShell>
              )}
            </div>
            {/* Step preview — derived from vervolgType/vervolgMomentDagen (no backend field). */}
            <ol className="entingen-steps">
              <li className="entingen-step entingen-step--done">
                <span className="entingen-step__dot entingen-step__dot--done" aria-hidden="true" />
                <span>
                  <strong>{t("entingenTab.vervolg.stap1")}</strong> {t("entingenTab.vervolg.stap1.value")}
                </span>
              </li>
              {showStap2 && (
                <li className="entingen-step">
                  <span className="entingen-step__dot" aria-hidden="true" />
                  <span>
                    <strong>{t("entingenTab.vervolg.stap2")}</strong>{" "}
                    {vervolgTypeNow === "ANDERE"
                      ? vervolgProductLabel !== dash && !editing
                        ? vervolgProductLabel
                        : t("entingenTab.vervolg.stap2.andere")
                      : t("entingenTab.vervolg.stap2.zelfde")}
                    {vervolgMomentNow && vervolgMomentNow.trim() !== "" && (
                      <> {t("entingenTab.vervolg.stap2.dagen", { args: [vervolgMomentNow] })}</>
                    )}
                  </span>
                </li>
              )}
            </ol>
            {showStap2 && (
              <p className="entingen-info entingen-info--green">{t("entingenTab.vervolg.info")}</p>
            )}
          </div>
        </Tile>

        {/* ── Overige instellingen ── */}
        <Tile title={t("entingenTab.section.overige")}>
          <div className="entingen-section">
            <FieldShell label={t("entingenTab.combineerbaar")}>
              <YesNoToggle
                checked={editing && form ? form.combineerbaar : (c.combineerbaar ?? false)}
                onChange={(v) => set("combineerbaar", v)}
                label={t("entingenTab.combineerbaar")}
                disabled={!editing}
              />
            </FieldShell>
            <FieldShell label={t("entingenTab.interneOpmerking")} span2>
              {editing && form ? (
                <textarea
                  className="fui-field__input"
                  rows={2}
                  value={form.interneOpmerking}
                  onChange={(e) => set("interneOpmerking", e.target.value)}
                />
              ) : (
                <span className="entingen-readonly">{c.interneOpmerking || dash}</span>
              )}
            </FieldShell>
            <FieldShell label={t("entingenTab.actief")}>
              <YesNoToggle
                checked={editing && form ? form.actief : (c.actief ?? false)}
                onChange={(v) => set("actief", v)}
                label={t("entingenTab.actief")}
                disabled={!editing}
              />
            </FieldShell>
          </div>
        </Tile>

        {/* Source parity: no tiles for `notities` / `geldigVanafBasis` / `geldigTotBerekening` — those
            keys exist only in the source DEFAULT_CONFIG without UI. Their values still round-trip via
            toForm + the save payload (nothing is lost); they are simply not editable here, as in the source. */}
      </div>

      <ActionBar>
        {noticeText && (
          <span className={`product-notice product-notice--${notice}`} role="status">
            {noticeText}
          </span>
        )}
        {/* The tab opens directly editable (Jmix parity): the footer is always Annuleren · Opslaan.
            ROLE_PRODUCT_EDIT is enforced server-side (PUT → 403); no client authority signal, so the
            affordance stays enabled and a 403 surfaces as a save error. */}
        <Button variant="secondary" onClick={cancelEdit} disabled={saving}>
          {t("productEditView.cancel")}
        </Button>
        <Button variant="primary" onClick={save} disabled={saving || !form}>
          {t("productEditView.save")}
        </Button>
      </ActionBar>
    </div>
  );
}

function numToStr(n: number | null): string {
  return n == null ? "" : String(n);
}
